import { apiFetch } from './client.js';

/** The shapes core-banking stores a price in. Cents travel as strings. */
export type FeeType =
  | { kind: 'FLAT'; amountCents: string }
  | {
      kind: 'PERCENTAGE';
      basisPoints: number;
      /** Floor: what the customer pays when the percentage comes to less. */
      minAmountCents?: string;
      /** Ceiling, at the other end. */
      maxAmountCents?: string;
    }
  | {
      kind: 'TIER';
      tiers: Array<{ minAmountCents: string; maxAmountCents?: string; fee: FeeType }>;
    };

export interface CustomerFee {
  name: string;
  label: string;
  /** False when the platform prices this fee but does not yet take it. */
  charged: boolean;
  note: string;
  currency: string;
  /** Where the price in force came from. */
  source: 'OVERRIDE' | 'GLOBAL' | 'NONE';
  effective: {
    feeType: FeeType;
    summary: string;
    scheduleId?: string;
    version?: number;
    effectiveFrom?: string;
    setBy?: string;
    preview: Array<{ amountCents: string; feeCents: string }>;
  } | null;
  /** The standard price, kept alongside so a revert means something. */
  globalFeeType: FeeType | null;
}

export interface CustomerFeeChange {
  id: string;
  name: string;
  currency: string;
  action: 'CREATED' | 'OVERRIDDEN' | 'REVERTED' | 'DEACTIVATED';
  from: string | null;
  to: string | null;
  reason: string;
  actor: string;
  at: string;
}

/** Whether the one-off onboarding fee has been taken from this customer. */
export interface OnboardingFeeState {
  collected: boolean;
  amountCents?: string;
  currency?: string;
  /** ISO timestamp of the posting. */
  at?: string;
}

/**
 * What happened when the platform tried to take the onboarding fee. Every one
 * of these is a normal answer (HTTP 200); `ERROR` appears only after a price
 * change, when the attempt itself broke but the new price was still saved.
 */
export type OnboardingCollectionResult =
  | { outcome: 'COLLECTED'; amountCents: string; currency: string; accountId: string }
  | { outcome: 'ALREADY_COLLECTED'; amountCents?: string; currency?: string; accountId?: string; at?: string }
  | { outcome: 'NO_PRICE'; currency: string }
  | { outcome: 'NO_ACCOUNT'; currency: string }
  | { outcome: 'NOT_ACTIVE'; currency: string; accountId: string }
  | {
      outcome: 'INSUFFICIENT_FUNDS';
      currency: string;
      priceCents: string;
      availableCents: string;
      accountId: string;
    }
  | { outcome: 'NOT_COLLECTED'; currency: string; accountId: string }
  | { outcome: 'ERROR'; currency: string };

export const ONBOARDING_FEE_NAME = 'Onboarding Fee';

export const customerFeesApi = {
  list: (
    customerId: string,
    currency: string,
  ): Promise<{
    currency: string;
    /** Currencies this customer holds an account in; empty means unknown. */
    heldCurrencies: string[];
    /** Null when it could not be read — unknown, not "not collected". */
    onboarding: OnboardingFeeState | null;
    fees: CustomerFee[];
    history: CustomerFeeChange[];
  }> =>
    apiFetch(`/admin/customers/${customerId}/fees?currency=${encodeURIComponent(currency)}`),

  override: (
    customerId: string,
    input: { name: string; currency: string; feeType: FeeType; reason: string },
  ): Promise<{
    scheduleId: string;
    name: string;
    currency: string;
    version: number;
    /** Present when the Onboarding Fee was repriced: the collection retried. */
    onboardingCollection?: OnboardingCollectionResult;
  }> =>
    apiFetch(`/admin/customers/${customerId}/fees`, { method: 'PUT', body: input }),

  revert: (
    customerId: string,
    input: { name: string; currency: string; reason: string },
  ): Promise<{ reverted: boolean; onboardingCollection?: OnboardingCollectionResult }> =>
    apiFetch(`/admin/customers/${customerId}/fees`, { method: 'DELETE', body: input }),

  collectOnboarding: (customerId: string, currency: string): Promise<OnboardingCollectionResult> =>
    apiFetch(`/admin/customers/${customerId}/fees/onboarding/collect`, {
      method: 'POST',
      body: { currency },
    }),
};
